import { useState, useEffect } from 'react';
import { Search, Eye, Truck, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'confirmed', label: 'Confirmed', color: 'bg-blue-100 text-blue-800' },
  { value: 'packed', label: 'Packed', color: 'bg-purple-100 text-purple-800' },
  { value: 'shipped', label: 'Shipped', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'delivered', label: 'Delivered', color: 'bg-green-100 text-green-800' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800' },
];

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [updateForm, setUpdateForm] = useState({ status: '', tracking_number: '', courier: '' });
  const token = localStorage.getItem('chaleur_admin_token');

  useEffect(() => {
    fetchOrders();
  }, [currentPage, statusFilter]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, limit: 20 };
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      
      const response = await axios.get(`${API}/admin/orders`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data.orders || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setUpdateForm({
      status: order.status,
      tracking_number: order.tracking_number || '',
      courier: order.courier || ''
    });
    setShowDetail(true);
  };

  const handleUpdateStatus = async () => {
    try {
      await axios.put(`${API}/admin/orders/${selectedOrder.id}/status`, null, {
        params: updateForm,
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order updated');
      setShowDetail(false);
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const getStatusColor = (status) => {
    return STATUS_OPTIONS.find(s => s.value === status)?.color || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6" data-testid="admin-orders">
      <div>
        <h1 className="text-2xl font-light mb-2">Orders</h1>
        <p className="text-muted-foreground">Manage customer orders</p>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] rounded-none">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUS_OPTIONS.map(s => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Orders Table */}
      <Card className="rounded-none border-gray-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto" />
            </div>
          ) : orders.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-3 px-4 text-sm font-medium">Order</th>
                    <th className="text-left py-3 px-4 text-sm font-medium">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-medium">Customer</th>
                    <th className="text-left py-3 px-4 text-sm font-medium">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium">Payment</th>
                    <th className="text-left py-3 px-4 text-sm font-medium">Total</th>
                    <th className="text-left py-3 px-4 text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(order => (
                    <tr key={order.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <span className="font-medium">{order.order_number}</span>
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {order.shipping_address?.first_name} {order.shipping_address?.last_name}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 text-xs ${getStatusColor(order.status)}`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 text-xs ${
                          order.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {order.payment_status}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-medium">
                        ₹{order.total?.toLocaleString()}
                      </td>
                      <td className="py-3 px-4">
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-none"
                          onClick={() => handleViewOrder(order)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              No orders found
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          {[...Array(totalPages)].map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentPage(idx + 1)}
              className={`w-10 h-10 flex items-center justify-center text-sm ${
                currentPage === idx + 1 
                  ? 'bg-foreground text-primary-foreground' 
                  : 'border border-gray-200 hover:border-primary'
              }`}
            >
              {idx + 1}
            </button>
          ))}
        </div>
      )}

      {/* Order Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-2xl rounded-none">
          <DialogHeader>
            <DialogTitle>Order {selectedOrder?.order_number}</DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6">
              {/* Order Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Date</Label>
                  <p>{new Date(selectedOrder.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Total</Label>
                  <p className="text-xl font-medium">₹{selectedOrder.total?.toLocaleString()}</p>
                </div>
              </div>

              {/* Shipping Address */}
              <div>
                <Label className="text-muted-foreground mb-2 block">Shipping Address</Label>
                <div className="p-4 bg-gray-50 text-sm">
                  <p className="font-medium">{selectedOrder.shipping_address?.first_name} {selectedOrder.shipping_address?.last_name}</p>
                  <p>{selectedOrder.shipping_address?.address_line1}</p>
                  {selectedOrder.shipping_address?.address_line2 && <p>{selectedOrder.shipping_address?.address_line2}</p>}
                  <p>{selectedOrder.shipping_address?.city}, {selectedOrder.shipping_address?.state} {selectedOrder.shipping_address?.postal_code}</p>
                  <p>{selectedOrder.shipping_address?.country}</p>
                  <p className="mt-2">{selectedOrder.shipping_address?.phone}</p>
                </div>
              </div>

              {/* Items */}
              <div>
                <Label className="text-muted-foreground mb-2 block">Items</Label>
                <div className="space-y-2">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm p-2 bg-gray-50">
                      <span>{item.product_name} × {item.quantity}</span>
                      <span>₹{item.line_total?.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Update Status */}
              <div className="pt-4 border-t border-gray-200 space-y-4">
                <h4 className="font-medium">Update Order</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="mb-2 block">Status</Label>
                    <Select value={updateForm.status} onValueChange={(v) => setUpdateForm({ ...updateForm, status: v })}>
                      <SelectTrigger className="rounded-none">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {STATUS_OPTIONS.map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-2 block">Tracking Number</Label>
                    <Input
                      value={updateForm.tracking_number}
                      onChange={(e) => setUpdateForm({ ...updateForm, tracking_number: e.target.value })}
                      placeholder="AWB Number"
                      className="rounded-none"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block">Courier</Label>
                    <Input
                      value={updateForm.courier}
                      onChange={(e) => setUpdateForm({ ...updateForm, courier: e.target.value })}
                      placeholder="e.g. Shiprocket"
                      className="rounded-none"
                    />
                  </div>
                </div>
                <Button onClick={handleUpdateStatus} className="rounded-none bg-foreground text-primary-foreground">
                  Update Order
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOrders;
